import React from "react";
import { withRouter } from "react-router-dom";
import CONSTANT from "../../constant";
import Loader from "../../component/common/Loader";
import { getEmployeeFullReport } from "../../actions/AppraisalCycleAction";
import { formatDateDDMMYY } from "../../utils";
import {
  APAR_PRINT_ROOT_ID,
  aparPdfFileName,
  clearAparPrintRoot,
  downloadAparReportPdf,
  markAparPrintRoot,
  printAparReport,
} from "./aparPrintUtils";

// ─── shared print/paper styles (mirrors HrAparFullReport) ───────────────────
const paperStyle: React.CSSProperties = {
  fontFamily: "Times New Roman, serif",
  fontSize: "15px",
  color: "#000",
  lineHeight: "1.6",
};

const sectionBoxStyle: React.CSSProperties = {
  border: "1px solid #000",
  padding: "14px 18px",
  marginBottom: "18px",
};

const tableBorderStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
};

const tdStyle: React.CSSProperties = {
  border: "1px solid #000",
  padding: "7px 10px",
  verticalAlign: "top",
};

const thStyle: React.CSSProperties = {
  ...tdStyle,
  fontWeight: "bold",
  textAlign: "center",
  backgroundColor: "#f0f0f0",
};

const questionLabelStyle: React.CSSProperties = {
  fontWeight: "bold",
  marginBottom: "0px",
  backgroundColor: "#1a237e",
  color: "#ffffff",
  padding: "8px 14px",
  fontSize: "14px",
  letterSpacing: "0.2px",
};

const answerBoxStyle: React.CSSProperties = {
  border: "2px solid #9fa8da",
  borderTop: "none",
  padding: "10px 14px",
  backgroundColor: "#ffffff",
  color: "#222",
};

const underlineBlank = (width = "200px") => (
  <span
    style={{
      display: "inline-block",
      borderBottom: "1px solid #000",
      minWidth: width,
      marginLeft: "6px",
    }}
  />
);

const val = (v: any, fallback = "") =>
  v !== undefined && v !== null && v !== "" ? v : fallback;

const blankOr = (v: any, width = "180px") =>
  v ? (
    <span style={{ marginLeft: "6px", fontWeight: "normal" }}>{v}</span>
  ) : (
    underlineBlank(width)
  );

const fmtDate = (v: any) => {
  if (!v) return "";
  const formatted = formatDateDDMMYY(v);
  return formatted || v;
};

const INTEGRITY_LABELS: any = {
  beyond_doubt: "Beyond doubt",
  doubtful: "Integrity is doubtful (secret note attached)",
  nothing_adverse: "Nothing adverse received",
};

class EmployeeCompletedReport extends React.Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      showLoader: true,
      reportData: null,
      error: null,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    document.title = "APAR Full Report - " + CONSTANT.organisation.name;

    const locationState = this.props.location?.state || {};
    const { employeeCode, appraisalCycleId, periodId } = locationState;

    if (!employeeCode || !appraisalCycleId) {
      this.setState({ showLoader: false, error: "Missing employee or cycle information." });
      return;
    }

    getEmployeeFullReport({ employeeCode, appraisalCycleId, periodId }).then((res: any) => {
      if (res?.result) {
        // Tag the report's ancestor chain once it is on screen, so both the
        // Print button and the browser's own Ctrl+P print the sheet alone.
        this.setState({ reportData: res.result, showLoader: false }, markAparPrintRoot);
      } else {
        this.setState({ showLoader: false, error: "No report data available." });
      }
    }).catch(() => {
      this.setState({ showLoader: false, error: "Failed to load report." });
    });
  }

  componentWillUnmount() {
    clearAparPrintRoot();
  }

  handlePrint = () => printAparReport();

  handleDownloadPdf = () => {
    const empName = this.state.reportData?.employee?.name;
    const cycleName = this.state.reportData?.cycle?.financialYear;
    downloadAparReportPdf(aparPdfFileName(empName, cycleName));
  };

  // ─── Section I ────────────────────────────────────────────────────────────

  renderSectionI = () => {
    const { reportData } = this.state;
    const emp = reportData?.employee || {};
    const period = reportData?.period || (reportData?.periods || [])[0] || {};
    const managers = period.managers || {};
    const otherDetails = period.otherDetails || reportData?.otherDetails || {};
    const kpiReport = period.kpiReport;
    const mouTask = kpiReport?.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport?.tasks?.filter((t: any) => t.particular !== "*)") || [];

    const leaveAbsences: any[] = period.leaveAbsences || reportData?.leaveAbsences || [];
    const qualifications: any[] = period.qualifications || reportData?.qualifications || [];
    const trainingProgrammes: any[] = period.trainingProgrammes || reportData?.trainingProgrammes || [];

    const rmPeriodFrom = fmtDate(managers.reportingManager?.periodFrom || managers.reportingManager?.period_from);
    const rmPeriodTo = fmtDate(managers.reportingManager?.periodTo || managers.reportingManager?.period_to);
    const rvmPeriodFrom = fmtDate(managers.reviewingManager?.periodFrom || managers.reviewingManager?.period_from);
    const rvmPeriodTo = fmtDate(managers.reviewingManager?.periodTo || managers.reviewingManager?.period_to);
    const aaPeriodFrom = fmtDate(managers.acceptingAuthority?.periodFrom || managers.acceptingAuthority?.period_from);
    const aaPeriodTo = fmtDate(managers.acceptingAuthority?.periodTo || managers.acceptingAuthority?.period_to);

    return (
      <div style={paperStyle}>
        {/* Section heading — centered */}
        <div style={{ textAlign: "center", marginBottom: "10px" }}>
          <div style={{ display: "inline-block", border: "1px solid #000", padding: "8px 16px", fontWeight: "bold", fontSize: "16px", letterSpacing: "1px", color: "#00008B" }}>
            SECTION I — BASIC INFORMATION
          </div>
        </div>

        {/* "Personal Data" heading + photo box in the same row */}
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "8px" }}>
          <div style={{ flex: 1, textAlign: "center", fontWeight: "bold", fontSize: "15px", color: "#00008B", textDecoration: "underline", paddingTop: "8px" }}>
            Personal Data of the officer reported upon
          </div>
          <div style={{ flexShrink: 0, border: "1px solid #000", width: "120px", height: "110px", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: "13px", fontWeight: "bold", color: "#00008B", padding: "6px", marginLeft: "16px" }}>
            Recent Photograph of the officer reported upon to be affixed
          </div>
        </div>

        <div style={sectionBoxStyle}>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>1.</span>&nbsp; Name of the Officer reported upon:{" "}
            {blankOr(emp.name, "280px")}
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>2.</span>&nbsp; Employee Number:{" "}
            {blankOr(emp.employeeNumber || emp.employee_number, "200px")}
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>3.</span>&nbsp; Date of Birth:{" "}
            {blankOr(fmtDate(emp.dateOfBirth || emp.date_of_birth), "160px")}
          </div>
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>4.</span>&nbsp; Brief Academic &amp; Professional Qualifications :
            <div style={{ borderBottom: "1px solid #000", minHeight: "24px", marginTop: "6px", padding: "2px 6px" }}>
              {val(emp.academicQualifications || emp.academic_qualifications, "")}
            </div>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <div style={{ marginBottom: "4px" }}>
              <span style={{ fontWeight: "bold" }}>5.&nbsp;&nbsp;(a)</span>&nbsp; Name of the Post held:
            </div>
            <div style={{ borderBottom: "1px solid #000", minHeight: "24px", marginBottom: "10px", marginLeft: "52px", padding: "2px 6px" }}>
              {val(emp.postHeld || emp.post_held || emp.designation, "")}
            </div>
            <div style={{ marginBottom: "6px" }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>(b)</span>&nbsp; Grade of Post held:{" "}
              {blankOr(emp.gradeOfPost || emp.grade_of_post, "120px")}
            </div>
            <div style={{ marginBottom: "6px" }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>(c)</span>&nbsp; Date of Continuous Appointment in this Post:{" "}
              {blankOr(fmtDate(emp.continuousAppointmentDate || emp.dateContinuousAppointmentPost || emp.date_continuous_appointment_post), "140px")}
            </div>
            <div style={{ marginBottom: "6px" }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>(d)</span>&nbsp; Present Pay and Scale of Pay:{" "}
              {blankOr(
                emp.presentPayScale || emp.presentPay || emp.present_pay
                  ? `${emp.presentPayScale || emp.presentPay || emp.present_pay}${(!emp.presentPayScale && (emp.scaleOfPay || emp.scale_of_pay)) ? " | Scale: " + (emp.scaleOfPay || emp.scale_of_pay) : ""}`
                  : "",
                "200px"
              )}
            </div>
            <div style={{ marginBottom: "4px" }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>(e)</span>&nbsp; Date of continuous Appointment in the same enterprise:{" "}
              {blankOr(fmtDate(emp.enterpriseAppointmentDate || emp.dateContinuousAppointmentEnterprise || emp.date_continuous_appointment_enterprise), "130px")}
            </div>
          </div>
          <div style={{ marginBottom: "4px" }}>
            <div style={{ marginBottom: "6px" }}>
              <span style={{ fontWeight: "bold" }}>6.&nbsp;&nbsp;(a)</span>&nbsp; Date of First Public Enterprise Appointment:{" "}
              {blankOr(fmtDate(emp.firstPublicEnterpriseDate || emp.dateFirstPublicEnterpriseAppointment || emp.date_first_public_enterprise_appointment), "160px")}
            </div>
            <div>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{ fontWeight: "bold" }}>(b)</span>&nbsp; Scale of Pay of the Post on First Appointment:{" "}
              {blankOr(emp.scaleOfPayFirstAppointment || emp.scale_of_pay_first_appointment, "160px")}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            7.&nbsp; Reporting, Reviewing and Accepting Authorities during the year
          </div>
          <table style={tableBorderStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "25%" }}></th>
                <th style={{ ...thStyle, width: "40%" }}>Name &amp; Designation</th>
                <th style={{ ...thStyle, width: "35%" }} colSpan={2}>Period worked</th>
              </tr>
              <tr>
                <th style={thStyle}></th>
                <th style={thStyle}></th>
                <th style={{ ...thStyle, width: "17.5%" }}>from</th>
                <th style={{ ...thStyle, width: "17.5%" }}>to</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Reporting Authority</td>
                <td style={tdStyle}>
                  {managers.reportingManager
                    ? `${managers.reportingManager.name || ""}${managers.reportingManager.designation ? " (" + managers.reportingManager.designation + ")" : ""}`
                    : ""}
                </td>
                <td style={tdStyle}>{rmPeriodFrom}</td>
                <td style={tdStyle}>{rmPeriodTo}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Reviewing Authority</td>
                <td style={tdStyle}>
                  {managers.reviewingManager
                    ? `${managers.reviewingManager.name || ""}${managers.reviewingManager.designation ? " (" + managers.reviewingManager.designation + ")" : ""}`
                    : ""}
                </td>
                <td style={tdStyle}>{rvmPeriodFrom}</td>
                <td style={tdStyle}>{rvmPeriodTo}</td>
              </tr>
              <tr>
                <td style={tdStyle}>Accepting Authority</td>
                <td style={tdStyle}>
                  {managers.acceptingAuthority
                    ? `${managers.acceptingAuthority.name || ""}${managers.acceptingAuthority.designation ? " (" + managers.acceptingAuthority.designation + ")" : ""}`
                    : ""}
                </td>
                <td style={tdStyle}>{aaPeriodFrom}</td>
                <td style={tdStyle}>{aaPeriodTo}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            8.&nbsp; Period of absence on leave, etc. during the year
          </div>
          <table style={tableBorderStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "30%" }}></th>
                <th style={{ ...thStyle, width: "25%" }}>Period</th>
                <th style={{ ...thStyle, width: "20%" }}>Type</th>
                <th style={{ ...thStyle, width: "25%" }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {leaveAbsences.length > 0 ? (
                leaveAbsences.map((la: any, i: number) => (
                  <tr key={i}>
                    <td style={tdStyle}>{la.leave_type_label || (i === 0 ? "On Leave other than Casual Leave" : "Others (specify)")}</td>
                    <td style={tdStyle}>{fmtDate(la.period_from)}{la.period_to ? ` – ${fmtDate(la.period_to)}` : ""}</td>
                    <td style={tdStyle}>{la.type || ""}</td>
                    <td style={tdStyle}>{la.remarks || ""}</td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td style={{ ...tdStyle, height: "30px" }}>On Leave other than Casual Leave</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, height: "30px" }}>Others (specify)</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            9.&nbsp; Qualification acquired and Training programmes attended during the year:
          </div>
          <div className="apar-q-label" style={questionLabelStyle}>(a) Details of Qualification acquired during the year</div>
          <table style={tableBorderStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "8%" }}>S. No.</th>
                <th style={{ ...thStyle, width: "30%" }}>Details of Qualification</th>
                <th style={{ ...thStyle, width: "30%" }}>Institution from which studied</th>
                <th style={{ ...thStyle, width: "32%" }}>Details of subjects studied and the marks obtained</th>
              </tr>
            </thead>
            <tbody>
              {qualifications.length > 0 ? (
                qualifications.map((q: any, i: number) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{i + 1}</td>
                    <td style={tdStyle}>{q.details || q.qualification_details || ""}</td>
                    <td style={tdStyle}>{q.institution || ""}</td>
                    <td style={tdStyle}>{q.subjects_marks || q.subjects || ""}</td>
                  </tr>
                ))
              ) : (
                [1, 2, 3].map((n) => (
                  <tr key={n}>
                    <td style={{ ...tdStyle, height: "28px", textAlign: "center" }}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div style={{ fontWeight: "bold", marginTop: "12px", marginBottom: "6px" }}>(b) Details of Training programme attended during the year</div>
          <table style={tableBorderStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "20%" }}>Date from</th>
                <th style={{ ...thStyle, width: "20%" }}>Date to</th>
                <th style={{ ...thStyle, width: "30%" }}>Institute</th>
                <th style={{ ...thStyle, width: "30%" }}>Subject</th>
              </tr>
            </thead>
            <tbody>
              {trainingProgrammes.length > 0 ? (
                trainingProgrammes.map((tp: any, i: number) => (
                  <tr key={i}>
                    <td style={tdStyle}>{fmtDate(tp.date_from)}</td>
                    <td style={tdStyle}>{fmtDate(tp.date_to)}</td>
                    <td style={tdStyle}>{tp.institute || ""}</td>
                    <td style={tdStyle}>{tp.subject || ""}</td>
                  </tr>
                ))
              ) : (
                [1, 2, 3].map((n) => (
                  <tr key={n}>
                    <td style={{ ...tdStyle, height: "28px" }}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            10.&nbsp; Awards / Honours received during the year
          </div>
          <div style={{ border: "1px solid #000", minHeight: "60px", padding: "8px" }}>
            {val(otherDetails.awards_honours || otherDetails.awardsHonours, "")}
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            11.&nbsp; Number of officers for whom PAR was not written by the officer reported upon as Reporting/Reviewing Authority for the previous year
          </div>
          <div style={{ border: "1px solid #000", minHeight: "60px", padding: "8px" }}>
            {val(otherDetails.par_not_written_count || otherDetails.parNotWrittenCount, "")}
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", color: "#00008B" }}>
            12.&nbsp;&nbsp;Date of filing the property return in the prescribed format for the year ending 31<sup>st</sup> December,{" "}
            {underlineBlank("80px")}.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "6px" }}>
            <div style={{ border: "1px solid #000", width: "140px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {fmtDate(otherDetails.property_return_date || otherDetails.propertyReturnDate)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", color: "#00008B" }}>
            13.&nbsp;&nbsp;Date of last prescribed medical examination (for officers over 40 years of age). Please attach a copy of the summary of the medical report. (Suggested summary format of the medical examination report is at Appendix I)
          </div>
          <div style={{ marginTop: "8px" }}>
            {fmtDate(otherDetails.last_medical_exam_date || otherDetails.lastMedicalExamDate)}
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#00008B" }}>
            14.&nbsp;&nbsp;Annual work plan and achievement
          </div>
          <table style={tableBorderStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "35%" }}>Tasks to be performed</th>
                <th style={{ ...thStyle, width: "15%" }}>Weightage</th>
                <th style={{ ...thStyle, width: "25%" }}>Deliverables<sup>1</sup></th>
                <th style={{ ...thStyle, width: "25%" }}>Achievement<sup>2</sup></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...tdStyle, fontWeight: "bold" }}>I – MoU Targets</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>*3</td>
                <td style={tdStyle}>{mouTask?.deliverables || ""}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>*4</td>
              </tr>
              <tr>
                <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={4}>II – Other Key assigned tasks flowing from KPI</td>
              </tr>
              {otherTasks.length > 0 ? (
                otherTasks.map((task: any, idx: number) => (
                  <tr key={task.id || idx}>
                    <td style={tdStyle}>{task.particular || String.fromCharCode(105 + idx) + ")"}&nbsp;&nbsp;{task.task_description || ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{task.weightage || ""}</td>
                    <td style={tdStyle}>{task.deliverables || ""}</td>
                    <td style={tdStyle}>{task.achievement || ""}</td>
                  </tr>
                ))
              ) : (
                ["i)", "ii)", "iii)", "iv)", "v)"].map((lbl) => (
                  <tr key={lbl}>
                    <td style={{ ...tdStyle, height: "26px" }}>{lbl}</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                ))
              )}
              <tr>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: "bold" }}>Total (i to v)</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>*5</td>
                <td style={tdStyle}></td>
                <td style={tdStyle}></td>
              </tr>
              <tr>
                <td style={{ ...tdStyle, fontWeight: "bold" }}>III&nbsp;&nbsp;&nbsp;&nbsp;Grand&nbsp;&nbsp;Total</td>
                <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>75</td>
                <td style={tdStyle}></td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: "10px", fontSize: "13px", color: "#333" }}>
            <div><sup>1</sup> Deliverables refer to quantitative or financial targets or verbal description of expected outputs.</div>
            <div><sup>2</sup> Actual achievements refer to the achievements against the specified deliverables in respect of each task.</div>
            <div><sup>3</sup> The weightage for MoU targets is 75 for Chief Executives, 50 for Functional Directors and Executives at E9 and E8 level.</div>
            <div><sup>4</sup> The final MoU score based on audited accounts conveyed by DPE should be filled in this space.</div>
            <div><sup>5</sup> The weightage for other assigned tasks flowing from KPI is nil for Chief Executives and 25 for Functional Director &amp; Executives at E9 &amp; E8 level.</div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          <div />
          <div style={{ textAlign: "center" }}>
            <div style={{ border: "1px solid #000", width: "140px", height: "40px", marginBottom: "6px" }} />
            <div><strong>Signature:</strong></div>
            <div style={{ marginTop: "8px" }}>
              <strong>Name &amp; Designation of the officer of the</strong>
            </div>
            <div><strong>Human Resource/Personnel/Administration Department</strong></div>
            <div style={{ marginTop: "6px" }}><strong>Date:</strong> {underlineBlank("100px")}</div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Section II – Self Appraisal ──────────────────────────────────────────

  renderSectionII = (period: any) => {
    const selfAppraisal = period.selfAppraisal;
    const kpiReport = period.kpiReport;
    const mouTask = kpiReport?.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport?.tasks?.filter((t: any) => t.particular !== "*)") || [];

    return (
      <div style={paperStyle}>
        <div style={{ textAlign: "center", marginBottom: "14px" }}>
          <div style={{ display: "inline-block", border: "1px solid #000", padding: "8px 16px", fontWeight: "bold", fontSize: "16px", letterSpacing: "1px", color: "#00008B", textDecoration: "underline" }}>
            Section II – Self-appraisal of the officer reported upon
          </div>
        </div>

        {!selfAppraisal ? (
          <p style={{ color: "#888", fontStyle: "italic" }}>Employee has not submitted self appraisal details.</p>
        ) : (
          <>
            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                <strong>1.&nbsp;&nbsp;&nbsp;&nbsp;Brief description of responsibilities:</strong>
              </div>
              <div style={{ fontStyle: "italic", fontSize: "13px", color: "#555", marginTop: "4px", marginBottom: "4px", marginLeft: "14px" }}>
                (Objectives of the position you hold and the responsibilities you are required to discharge, in about 100 words)
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, borderTop: "2px solid #9fa8da", minHeight: "80px" }}>
                {selfAppraisal.brief_description || selfAppraisal.responsibilities || ""}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>2.&nbsp;&nbsp;&nbsp;&nbsp;Annual work plan and achievement:</strong>
              </div>
              <table style={tableBorderStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: "35%" }}>Tasks to be performed</th>
                    <th style={{ ...thStyle, width: "15%" }}>Weightage</th>
                    <th style={{ ...thStyle, width: "25%" }}>Deliverables<sup>6</sup></th>
                    <th style={{ ...thStyle, width: "25%" }}>Achievement<sup>7</sup></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>I – MoU Targets</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>*8</td>
                    <td style={tdStyle}>{mouTask?.deliverables || ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>*9</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={4}>II – Other Key assigned tasks flowing from KPI</td>
                  </tr>
                  {otherTasks.length > 0 ? (
                    otherTasks.map((task: any, idx: number) => (
                      <tr key={task.id || idx}>
                        <td style={tdStyle}>
                          {task.particular || String.fromCharCode(105 + idx) + ")"}&nbsp;&nbsp;{task.task_description || ""}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{task.weightage || ""}</td>
                        <td style={tdStyle}>{task.deliverables || ""}</td>
                        <td style={tdStyle}>{task.achievement || ""}</td>
                      </tr>
                    ))
                  ) : (
                    ["i)", "ii)", "iii)", "iv)", "v)"].map((lbl) => (
                      <tr key={lbl}>
                        <td style={{ ...tdStyle, height: "26px" }}>{lbl}</td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}></td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: "bold" }}>Total (i to v)</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>*10</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>III&nbsp;&nbsp;&nbsp;&nbsp;Grand&nbsp;&nbsp;Total</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>75</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                <strong>3.</strong>&nbsp;&nbsp;&nbsp;&nbsp;
                <strong>During the period under report, do you believe that you have made any exceptional contribution, e.g. successful completion of an extraordinarily challenging task or major systemic improvement (resulting in significant benefits to the Company and/or reduction in time and costs)? If so, please give a verbal description (within 100 words):</strong>
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "80px" }}>
                {selfAppraisal.exceptional_contribution || ""}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                <strong>4.</strong>&nbsp;&nbsp;&nbsp;&nbsp;
                <strong>What are the constraints that hindered your performance?</strong>
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "80px" }}>
                {selfAppraisal.constraints_hindered || ""}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                <strong>5.</strong>&nbsp;&nbsp;&nbsp;&nbsp;
                <strong>Please indicate specific areas of training that will add value to you:</strong>
              </div>
              <div style={{ border: "1px solid #000" }}>
                <div style={{ borderBottom: "1px solid #000", padding: "8px", minHeight: "60px" }}>
                  <div style={{ color: "#8B0000", marginBottom: "4px", fontSize: "13px" }}>For the current assignment:</div>
                  {selfAppraisal.training_current_assignment || ""}
                </div>
                <div style={{ padding: "8px", minHeight: "60px" }}>
                  <div style={{ color: "#8B0000", marginBottom: "4px", fontSize: "13px" }}>For your future career:</div>
                  {selfAppraisal.training_future_career || ""}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div style={{ marginBottom: "8px" }}>
                <strong>6.</strong>&nbsp;&nbsp;&nbsp;&nbsp;<strong>Declaration</strong>
              </div>
              <table style={tableBorderStyle}>
                <tbody>
                  <tr>
                    <td style={{ ...tdStyle, width: "60%" }}>
                      Have you filed your immovable property return in the prescribed format as due? If yes, please mention the date.
                    </td>
                    <td style={{ ...tdStyle, width: "12%", textAlign: "center" }}>
                      {selfAppraisal.immovable_property_filed || "Yes/No"}
                    </td>
                    <td style={{ ...tdStyle, width: "28%" }}>
                      {selfAppraisal.immovable_property_date ? `Date: ${fmtDate(selfAppraisal.immovable_property_date)}` : ""}
                    </td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>Have you undergone the suggested medical check up?</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {selfAppraisal.medical_check_up || "Yes/No"}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold", textDecoration: "underline" }}>
                      Appendix I
                    </td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>
                      Have you set the annual work plan for all officers for the current year, in respect of whom you are the Reporting Authority?
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {selfAppraisal.annual_work_plan_set || "Yes/No"}
                    </td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ textAlign: "right", marginTop: "30px" }}>
              <div style={{ fontWeight: "bold" }}>Signature of the officer reported upon</div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <strong>Date:</strong>&nbsp;{underlineBlank("120px")}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── Section III – Reporting Authority ────────────────────────────────────

  renderSectionIII = (period: any) => {
    const assessment = period.reportingAssessment;
    const kpiReport = period.kpiReport;
    const repKpiGrades = period.reportingKpiTaskGrades || [];
    const revKpiGrades = period.reviewingKpiTaskGrades || [];
    const repAttrGrades = period.reportingPersonalAttributeGrades || [];
    const revAttrGrades = period.reviewingPersonalAttributeGrades || [];

    const repKpiMap: any = {};
    repKpiGrades.forEach((g: any) => { repKpiMap[g.kpi_task_id] = g; });
    const revKpiMap: any = {};
    revKpiGrades.forEach((g: any) => { revKpiMap[g.kpi_task_id] = g; });
    const repAttrMap: any = {};
    repAttrGrades.forEach((g: any) => { repAttrMap[g.attribute_master_id] = g; });
    const revAttrMap: any = {};
    revAttrGrades.forEach((g: any) => { revAttrMap[g.attribute_master_id] = g; });

    const mouTask = kpiReport?.tasks?.find((t: any) => t.particular === "*)");
    const otherTasks = kpiReport?.tasks?.filter((t: any) => t.particular !== "*)") || [];

    const mouRepWG = mouTask && repKpiMap[mouTask.id] ? parseFloat(repKpiMap[mouTask.id].weighted_grade) || 0 : 0;
    const mouRevWG = mouTask && revKpiMap[mouTask.id] ? parseFloat(revKpiMap[mouTask.id].weighted_grade) || 0 : 0;
    const otherRepWG = otherTasks.reduce((s: number, t: any) => s + (repKpiMap[t.id] ? parseFloat(repKpiMap[t.id].weighted_grade) || 0 : 0), 0);
    const otherRevWG = otherTasks.reduce((s: number, t: any) => s + (revKpiMap[t.id] ? parseFloat(revKpiMap[t.id].weighted_grade) || 0 : 0), 0);

    const allAttrs: any[] = [];
    const seen: any = {};
    [...repAttrGrades, ...revAttrGrades].forEach((g: any) => {
      const key = g.attribute_master_id || g.attribute_name;
      if (!seen[key]) { seen[key] = true; allAttrs.push(g); }
    });
    const repAttrTotal = allAttrs.reduce((s: number, a: any) => s + (repAttrMap[a.attribute_master_id] ? parseFloat(repAttrMap[a.attribute_master_id].grade) || 0 : 0), 0);
    const revAttrTotal = allAttrs.reduce((s: number, a: any) => s + (revAttrMap[a.attribute_master_id] ? parseFloat(revAttrMap[a.attribute_master_id].grade) || 0 : 0), 0);

    return (
      <div style={paperStyle}>
        <div style={{ textAlign: "center", marginBottom: "14px" }}>
          <div style={{ textAlign: "center" as const, fontWeight: "bold", textDecoration: "underline", color: "#00008B", fontSize: "16px", marginBottom: "4px" }}>
            Section III - Appraisal of the Reporting Authority
          </div>
          <div style={{ fontSize: "13px", fontStyle: "italic" }}>
            (Please read the relevant instructions at Annexure II &amp; III before filling up this section)
          </div>
        </div>

        {!assessment ? (
          <p style={{ color: "#888", fontStyle: "italic" }}>Reporting Authority has not submitted their assessment yet.</p>
        ) : (
          <>
            {[
              { n: "1.", q: "Please state whether you agree with the responses relating to the accomplishments of the work plan as filled out in Section II. If not, please furnish factual details.", v: assessment.agree_accomplishments },
              { n: "2.", q: "Please comment on the claim (if any) made by the officer reported upon about his exceptional contribution.", v: assessment.exceptional_contribution_comment },
              { n: "3.", q: "Has the officer reported upon met with any significant shortfall in achieving the targets? If yes, please furnish factual details.", v: assessment.significant_shortfall },
              { n: "4.", q: "Do you agree with the constraints mentioned by the officer reported upon that had hindered his performance and, if so, to what extent?", v: assessment.agree_constraints },
              { n: "5.", q: "Do you agree with the competency up-gradation needs as identified by the officer?", v: assessment.agree_competency_needs },
            ].map(({ n, q, v }) => (
              <div key={n} style={{ marginBottom: "16px" }}>
                <div className="apar-q-label" style={questionLabelStyle}>{n}&nbsp;&nbsp;&nbsp;&nbsp;{q}</div>
                <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "80px" }}>{v || ""}</div>
              </div>
            ))}

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                6.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Assessment of the achievements made against the targets.{" "}</strong>
                <em style={{ fontWeight: "normal", fontSize: "13px" }}>
                  (Grades should be assigned on a scale of 1-10, in maximum of 2 decimal numbers, with 10 referring to the best grade and 1 to the lowest grade. Weightage to this Section will be 75%)
                </em>
              </div>
              <table style={tableBorderStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: "22%" }} rowSpan={3}>Particulars</th>
                    {/* rowSpan 2, not 3: the letter row below needs this column free for
                        (a), otherwise its 5 cells spill into a phantom 8th column
                        and every heading sits one column right of its data. */}
                    <th style={{ ...thStyle, width: "8%" }} rowSpan={2}>Weightage</th>
                    <th style={{ ...thStyle, width: "30%" }} colSpan={2}>Reporting Authority</th>
                    <th style={{ ...thStyle, width: "30%" }} colSpan={2}>Reviewing Authority</th>
                    <th style={{ ...thStyle, width: "10%" }} rowSpan={3}>Initials of Reviewing Authority</th>
                  </tr>
                  <tr>
                    <th style={{ ...thStyle, width: "15%" }}>Absolute Grade</th>
                    <th style={{ ...thStyle, width: "15%" }}>Weighted Grade</th>
                    <th style={{ ...thStyle, width: "15%" }}>Absolute Grade</th>
                    <th style={{ ...thStyle, width: "15%" }}>Weighted Grade</th>
                  </tr>
                  <tr>
                    <th style={thStyle}>(a)</th>
                    <th style={thStyle}>(b)</th>
                    <th style={thStyle}>(c = a x b)/100</th>
                    <th style={thStyle}>(d)</th>
                    <th style={thStyle}>(e = a x d)/100</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>I - MOU Targets</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>*1</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{mouTask && repKpiMap[mouTask.id] ? repKpiMap[mouTask.id].absolute_grade : ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{mouRepWG ? mouRepWG.toFixed(2) : ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{mouTask && revKpiMap[mouTask.id] ? revKpiMap[mouTask.id].absolute_grade : ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{mouRevWG ? mouRevWG.toFixed(2) : ""}</td>
                    <td style={tdStyle}></td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={7}>II&nbsp;&nbsp;Other Key assigned tasks flowing from KPI</td>
                  </tr>
                  {otherTasks.length > 0 ? (
                    otherTasks.map((task: any) => {
                      const rg = repKpiMap[task.id];
                      const rvg = revKpiMap[task.id];
                      const rwg = rg ? parseFloat(rg.weighted_grade) || 0 : 0;
                      const rvwg = rvg ? parseFloat(rvg.weighted_grade) || 0 : 0;
                      return (
                        <tr key={task.id}>
                          <td style={tdStyle}>{task.particular}&nbsp;&nbsp;{task.task_description || ""}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{task.weightage || ""}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{rg ? rg.absolute_grade : ""}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{rwg ? rwg.toFixed(2) : ""}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{rvg ? rvg.absolute_grade : ""}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{rvwg ? rvwg.toFixed(2) : ""}</td>
                          <td style={tdStyle}></td>
                        </tr>
                      );
                    })
                  ) : (
                    ["i)", "ii)", "iii)", "iv)", "v)"].map((lbl) => (
                      <tr key={lbl}>
                        <td style={{ ...tdStyle, height: "26px" }}>{lbl}</td>
                        <td style={tdStyle}></td><td style={tdStyle}></td><td style={tdStyle}></td>
                        <td style={tdStyle}></td><td style={tdStyle}></td><td style={tdStyle}></td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: "bold", color: "#8B0000" }}>Total (i to v)</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>*3</td>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{otherRepWG ? otherRepWG.toFixed(2) : ""}</td>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{otherRevWG ? otherRevWG.toFixed(2) : ""}</td>
                    <td style={tdStyle}></td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>Grand Total I &amp; II</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>75</td>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{(mouRepWG + otherRepWG).toFixed(2)}</td>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{(mouRevWG + otherRevWG).toFixed(2)}</td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                7.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Assessment of Personal Attributes and Functional Competencies</strong>{" "}
                <em style={{ fontWeight: "normal", fontSize: "13px" }}>
                  (Grades should be assigned on a scale of 1-10, in maximum of 2 decimal numbers, with 10 referring to the best grade and 1 to the lowest grade. Weightage to this Section will be 25%)
                </em>
              </div>
              <table style={tableBorderStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: "8%" }}>S. No.</th>
                    <th style={{ ...thStyle, width: "42%" }}>Particulars of Personal Attributes and Functional Competencies<sup>(a)</sup></th>
                    <th style={{ ...thStyle, width: "16%" }}>Grade by Reporting Authority</th>
                    <th style={{ ...thStyle, width: "16%" }}>Grade by Reviewing Authority</th>
                    <th style={{ ...thStyle, width: "18%" }}>Initials of Reviewing Authority</th>
                  </tr>
                </thead>
                <tbody>
                  {allAttrs.length > 0 ? (
                    allAttrs.map((attr: any, i: number) => (
                      <tr key={attr.attribute_master_id || i}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x"][i] || i + 1}</td>
                        <td style={tdStyle}>{attr.attribute_name || ""}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{repAttrMap[attr.attribute_master_id] ? repAttrMap[attr.attribute_master_id].grade : ""}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{revAttrMap[attr.attribute_master_id] ? revAttrMap[attr.attribute_master_id].grade : ""}</td>
                        <td style={tdStyle}></td>
                      </tr>
                    ))
                  ) : (
                    ["Effective communication skills", "Strategic orientation and Decision making ability", "Problem solving and Analytical ability", "Ability to develop and motivate team members", "Ability to coordinate and develop collaborative partnerships", "Innovation and change orientation", "Planning and Organizing", "Result orientation", "Business Acumen", "Role based functional competency"].map((attr, i) => (
                      <tr key={i}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x"][i]}</td>
                        <td style={tdStyle}>{attr}</td>
                        <td style={{ ...tdStyle, height: "26px" }}></td>
                        <td style={tdStyle}></td>
                        <td style={tdStyle}></td>
                      </tr>
                    ))
                  )}
                  <tr>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, fontWeight: "bold", color: "#8B0000" }}>Total (i to x)</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{repAttrTotal ? repAttrTotal.toFixed(2) : ""}</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold" }}>{revAttrTotal ? revAttrTotal.toFixed(2) : ""}</td>
                    <td style={tdStyle}></td>
                  </tr>
                  <tr>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, fontWeight: "bold" }}>Overall Grading of Personal Attributes and Functional competencies</td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                    <td style={tdStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                8.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Integrity</strong>{" "}
                <em style={{ fontWeight: "normal", fontSize: "13px" }}>
                  (Please comment on the integrity of the officer reported upon by choosing any one of the following options):
                </em>
              </div>
              <table style={{ ...tableBorderStyle, width: "70%" }}>
                <tbody>
                  {[
                    { key: "beyond_doubt", label: "Beyond doubt", roman: "i)" },
                    { key: "doubtful", label: "Integrity of the officer is doubtful. A separate secret note is attached.", roman: "ii)" },
                    { key: "nothing_adverse", label: "Nothing adverse has been received about the officer", roman: "iii)" },
                  ].map(({ key, label, roman }) => (
                    <tr key={key}>
                      <td style={{ ...tdStyle, width: "8%", textAlign: "center" }}>{roman}</td>
                      <td style={{ ...tdStyle, width: "72%" }}>{label}</td>
                      <td style={{ ...tdStyle, width: "20%", textAlign: "center" }}>
                        {assessment.integrity === key ? "✓" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                9.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Pen picture by Reporting Officer. Please comment (in about 100 words) on the overall qualities of the officer including areas of strengths and those which need improvements.</strong>
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "90px" }}>
                {assessment.pen_picture || ""}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                10.&nbsp;&nbsp;<strong>Overall grade (on a grade of 0 -100) based on the grades awarded in Item no. 6 &amp; 7.</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ border: "1px solid #000", width: "120px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "16px" }}>
                  {assessment.overall_grade != null ? parseFloat(assessment.overall_grade).toFixed(2) : ""}
                </div>
              </div>
            </div>

            <div style={{ textAlign: "right", marginTop: "30px" }}>
              <div style={{ fontWeight: "bold" }}>Signature of Reporting Authority</div>
              <div style={{ fontWeight: "bold" }}>Name &amp; Designation of the Reporting Authority</div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <strong>Date:</strong>&nbsp;{underlineBlank("120px")}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── Section IV – Reviewing Authority ────────────────────────────────────

  renderSectionIV = (period: any) => {
    const assessment = period.reviewingAssessment;

    return (
      <div style={paperStyle}>
        <div style={{ textAlign: "center", marginBottom: "14px" }}>
          <div style={{ textAlign: "center" as const, fontWeight: "bold", textDecoration: "underline", color: "#00008B", fontSize: "16px", marginBottom: "4px" }}>
            Section IV – Review by the Reviewing Authority
          </div>
          <div style={{ fontSize: "13px", fontStyle: "italic" }}>
            (Please read the relevant instructions at Annexure-II &amp; III before filling up this section)
          </div>
        </div>

        {!assessment ? (
          <p style={{ color: "#888", fontStyle: "italic" }}>Reviewing Authority has not submitted their assessment yet.</p>
        ) : (
          <>
            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                1.&nbsp;&nbsp;&nbsp;&nbsp;Do you agree with the assessment made by the Reporting officer with respect to discharge of responsibilities and various attributes of the officer reported upon in Section III?
              </div>
              <div style={{ marginTop: "4px" }}>
                <strong>
                  {assessment.agree_with_reporting != null
                    ? assessment.agree_with_reporting ? "Yes" : "No"
                    : "Yes/No"}
                </strong>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                2.&nbsp;&nbsp;&nbsp;&nbsp;Do you agree with the assessment of the Reporting officer in respect of extraordinary achievements and/or significant shortfalls of the officer reported upon?
              </div>
              <div style={{ marginTop: "4px" }}>
                <strong>
                  {(assessment.agree_extraordinary ?? assessment.agree_with_discharge_assessment) != null
                    ? (assessment.agree_extraordinary ?? assessment.agree_with_discharge_assessment) ? "Yes" : "No"
                    : "Yes / No"}
                </strong>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                3.&nbsp;&nbsp;&nbsp;&nbsp;In case of difference of opinion, details and reasons for the same may be given.
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "80px" }}>
                {assessment.difference_details || ""}
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                4.&nbsp;&nbsp;&nbsp;&nbsp;Comments, if any, on the pen picture written by the Reporting Authority.
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "80px" }}>
                {assessment.pen_picture_comment || ""}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                5.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Overall grade on a scale of 0 –100</strong>{" "}
                <em style={{ fontWeight: "normal", fontSize: "13px" }}>
                  (The overall grade should be computed by summing up the weighted average grade obtained in Item No.6 and 7 of Section III.)
                </em>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ border: "1px solid #000", width: "120px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "16px" }}>
                  {assessment.overall_grade != null ? parseFloat(assessment.overall_grade).toFixed(2) : ""}
                </div>
              </div>
            </div>

            <div style={{ textAlign: "right", marginTop: "30px" }}>
              <div>
                <strong>Signature of Reviewing Authority</strong>{" "}
                {underlineBlank("160px")}
              </div>
              <div style={{ marginTop: "6px", fontWeight: "bold" }}>
                Name &amp; Designation of the Reviewing Authority
              </div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <strong>Date:</strong>&nbsp;{underlineBlank("120px")}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── Section V – Accepting Authority ─────────────────────────────────────

  renderSectionV = (period: any) => {
    const assessment = period.acceptingAssessment;

    return (
      <div style={paperStyle}>
        <div style={{ textAlign: "center", marginBottom: "14px" }}>
          <div style={{ textAlign: "center" as const, fontWeight: "bold", textDecoration: "underline", color: "#00008B", fontSize: "16px", marginBottom: "4px" }}>
            Section V – Acceptance by the Accepting Authority
          </div>
          <div style={{ fontSize: "13px", fontStyle: "italic" }}>
            (Please read the relevant instructions at Annexure II &amp; III before filling up this section)
          </div>
        </div>

        {!assessment ? (
          <p style={{ color: "#888", fontStyle: "italic" }}>Accepting Authority has not submitted their assessment yet.</p>
        ) : (
          <>
            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                1.&nbsp;&nbsp;&nbsp;&nbsp;Is the overall grade given by the Reporting/Reviewing Authority is consistent with the pen picture given by them?
              </div>
              <div style={{ marginTop: "4px" }}>
                <strong>
                  {assessment.is_grade_consistent != null
                    ? assessment.is_grade_consistent ? "Yes" : "No"
                    : "Yes/No"}
                </strong>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                2.&nbsp;&nbsp;&nbsp;&nbsp;Do you agree with the remarks of the Reporting /Reviewing Authorities?
              </div>
              <div style={{ marginTop: "4px" }}>
                <strong>
                  {assessment.agree_with_authorities != null
                    ? assessment.agree_with_authorities ? "Yes" : "No"
                    : "Yes/No"}
                </strong>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                3.&nbsp;&nbsp;&nbsp;&nbsp;In case of difference of opinion, details thereof and reasons for the same may be given.
              </div>
              <div className="apar-a-box" style={{ ...answerBoxStyle, minHeight: "100px" }}>
                {assessment.difference_details || ""}
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div className="apar-q-label" style={questionLabelStyle}>
                4.&nbsp;&nbsp;&nbsp;&nbsp;<strong>Overall grade on a grade of 0 – 100</strong>{" "}
                <em style={{ fontWeight: "normal", fontSize: "13px" }}>
                  (Grades should be assigned on a scale of 0-100, with 100 referring to the best grade and 0 to the lowest grade).
                </em>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ border: "1px solid #000", width: "120px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "16px" }}>
                  {assessment.overall_grade != null ? parseFloat(assessment.overall_grade).toFixed(2) : ""}
                </div>
              </div>
            </div>

            <div style={{ textAlign: "right", marginTop: "30px" }}>
              <div style={{ fontWeight: "bold" }}>Signature of Accepting Authority</div>
              <div style={{ fontWeight: "bold" }}>Name &amp; Designation of the Accepting Authority</div>
            </div>
            <div style={{ marginTop: "16px" }}>
              <strong>Date:</strong>&nbsp;{underlineBlank("120px")}
            </div>
          </>
        )}
      </div>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────────

  render() {
    const { showLoader, reportData, error } = this.state;
    const emp = reportData?.employee || {};
    const cycle = reportData?.cycle || {};
    const period = reportData?.period || (reportData?.periods || [])[0] || {};

    return (
      <div className="container-fluid mb-3">
        <div className="row">
          <div className="col-md-12">
            <div className="card homedob">

              <div className="row mb-3 no-print">
                <div className="col-md-6">
                  <h1 className="page-title">
                    <i className="fa fa-file-text-o text-primary mr-2" />
                    APAR Full Report
                  </h1>
                  {cycle.cycleName && (
                    <p className="text-muted mb-0">
                      {cycle.cycleName} | Financial Year: {cycle.financialYear}
                    </p>
                  )}
                </div>
                <div className="col-md-6 text-right d-flex align-items-center justify-content-end" style={{ gap: "8px" }}>
                  <button
                    className="btn btn-sm submit-button"
                    onClick={this.handlePrint}
                    title="Print this report"
                  >
                    <i className="fa fa-print mr-1" /> Print
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={this.handleDownloadPdf}
                    title="Download as PDF"
                    style={{ background: "radial-gradient(#27ae60, #1e8449)", color: "#fff", fontSize: "0.875rem" }}
                  >
                    <i className="fa fa-file-pdf-o mr-1" /> Download PDF
                  </button>
                  <button
                    className="btn btn-sm cancel-button"
                    onClick={() => this.props.history.goBack()}
                    title="Go back"
                  >
                    <i className="fa fa-arrow-left mr-1" /> Back
                  </button>
                </div>
              </div>

              {showLoader && <Loader />}

              {error && !showLoader && (
                <div className="alert alert-danger mt-3">
                  <i className="fa fa-exclamation-triangle mr-2" />
                  {error}
                </div>
              )}

              {!showLoader && reportData && (
                <div className="apar-paper-wrap">
                <div
                  id={APAR_PRINT_ROOT_ID}
                  className="apar-paper section-to-print"
                >
                  <div style={{ textAlign: "center", marginBottom: "22px", fontFamily: "Times New Roman, serif" }}>
                    <div style={{ fontWeight: "bold", fontSize: "20px", letterSpacing: "1.5px", color: "#00008B" }}>
                      ANNUAL PERFORMANCE APPRAISAL REPORT (APAR)
                    </div>
                    <div style={{ fontSize: "15px", marginTop: "6px" }}>
                      {cycle.cycleName ? `${cycle.cycleName} | ` : ""}Financial Year: {cycle.financialYear || ""}
                    </div>
                    {emp.name && (
                      <div style={{ fontSize: "15px", marginTop: "4px" }}>
                        Officer: <strong>{emp.name}</strong>
                        {emp.employeeNumber ? ` (${emp.employeeNumber})` : ""}
                      </div>
                    )}
                  </div>

                  <hr style={{ borderTop: "2px solid #000", marginBottom: "24px" }} />

                  {this.renderSectionI()}

                  <div className="apar-section-break" />

                  {this.renderSectionII(period)}

                  <div className="apar-section-break" />

                  {this.renderSectionIII(period)}

                  <div className="apar-section-break" />

                  {this.renderSectionIV(period)}

                  <div className="apar-section-break" />

                  {this.renderSectionV(period)}
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(EmployeeCompletedReport);
